import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import { AppState } from "@/store";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import {
  MessageCircle,
  Share2,
  MoreHorizontal,
  ThumbsUp,
  Edit2,
  Trash2,
  X,
  Check,
  Smile,
  Calendar,
  MapPin,
  Users,
  Clock,
  UserCheck,
  Rss,
  Send,
  Image as ImageIcon,
  Video as VideoIcon,
  Tag,
  Maximize2,
  Loader2,
} from "lucide-react";
import { LinkedinIcon as Linkedin } from "@/components/icons/LinkedinIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { CommentSection } from "./CommentSection";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Tiptap, { TiptapRef } from "@/components/Tiptap";
import { mapMediaUrls } from "@/utils/community";
import { searchUsers, SearchUser } from "@/api/community";
import { useToast } from "@/components/ui/use-toast";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import ShadCNDatePicker from "@/components/ui/datePicker";
import { MentionRenderer } from "./MentionRenderer";
import { ReportDialog } from "./ReportDialog";
import { Flag } from "lucide-react";

// Import the Post type from API
import {
  Post as APIPost,
  getCommentsByPost,
  getPostReactions,
  ReactionUser,
  getPostShares,
  ShareUser,
  ReactionStat,
} from "@/api/community";
import { uploadFileToServer } from "@/utils/upload";
import { usePosthog } from "@/hooks/usePosthog";
import { formatTimestamp } from "@/utils/datetime";
import { useTranslation } from "react-i18next";
import { CONSTRUCTION_EMOJIS, COMMON_EMOJIS, POST_REACTIONS } from "@/constants";

// Use API Post type directly
export type PostData = APIPost & {
  // Add local-only fields if needed
  timestamp: Date; // Converted from created_at
  isEdited?: boolean; // Derived from edit_history
  editedAt?: Date; // From edit_history
  isEvent?: boolean;
  eventDetails?: {
    title: string;
    date: Date;
    endDate?: Date;
    location: string;
    maxParticipants?: number;
    description?: string;
  };
  eventParticipants?: Array<{
    userId: string;
    userName: string;
    userTitle: string;
    userType?: string;
    organizationId?: string;
    photoUrl?: string;
    registeredAt: Date;
  }>;
  eventStatus?: "active" | "cancelled";
  isShared?: boolean;
  originalAuthor?: APIPost["author"];
  originalPostId?: string;
  sharedAt?: Date;
  shared_post?: {
    full_name: string;
    email: string;
    type: string;
    photo_thumbnail_url: string;
    job_title: string;
    id: string;
    author_id?: string; // Original event post author ID
    content?: string; // Original post content
    media_urls?: string[]; // Original post media
    tagged_users?: any[]; // Original post tagged users
    deleted?: boolean; // If the original post was deleted
    deleted_at?: Date; // When the original post was deleted
    isEvent?: boolean;
    eventDetails?: {
      title: string;
      date: Date;
      endDate?: Date;
      location: string;
      maxParticipants?: number;
      description?: string;
    };
    eventParticipants?: Array<{
      userId: string;
      userName: string;
      userTitle: string;
      userType?: string;
      organizationId?: string;
      photoUrl?: string;
      registeredAt: Date;
    }>;
    eventStatus?: "active" | "cancelled";
  };
  sharedContent?: string; // Content added by the person who shared the post
  // For local state management (override API types)
  likes?: Array<{ userId: string; userName: string; emoji: string }>;
  comments?: Array<{
    id: string;
    author: { name: string; avatar?: string; title: string };
    content: string;
    timestamp: Date;
    isEdited?: boolean;
    editedAt?: Date;
    likes: Array<{ userId: string; userName: string; emoji: string }>;
    replies: Array<{
      id: string;
      author: { name: string; avatar?: string; title: string };
      content: string;
      timestamp: Date;
      isEdited?: boolean;
      editedAt?: Date;
      likes: Array<{ userId: string; userName: string; emoji: string }>;
    }>;
  }>;
};

interface PostProps {
  post: PostData;
  onLike?: (postId: string, emoji: string) => void;
  onComment?: (postId: string, comment: string, mentionedUserIds?: string[]) => void;
  onShare?: (postId: string) => void;
  onEdit?: (
    postId: string,
    updates: {
      content: string;
      media_urls: string[];
      tagged_users: string[];
      eventDetails?: {
        title: string;
        date: Date;
        endDate?: Date;
        location: string;
        maxParticipants?: number;
        description?: string;
      };
    },
  ) => void;
  onDelete?: (postId: string, reason?: string, postType?: string, isEvent?: boolean) => void;
  onEditComment?: (
    postId: string,
    commentId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => void;
  onDeleteComment?: (postId: string, commentId: string) => void;
  onAddReply?: (postId: string, commentId: string, reply: string) => void;
  onEditReply?: (
    postId: string,
    commentId: string,
    replyId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => void;
  onDeleteReply?: (postId: string, commentId: string, replyId: string) => void;
  onJoinEvent?: (postId: string) => void;
  onLeaveEvent?: (postId: string) => void;
  onCancelEvent?: (postId: string) => void;
  onEditEvent?: (
    postId: string,
    eventDetails: {
      title: string;
      date: Date;
      endDate?: Date;
      location: string;
      maxParticipants?: number;
      description?: string;
    },
  ) => void;
  currentUserId?: string;
  showCommentsInitially?: boolean;
  autoExpandCommentId?: string;
  highlightReplyId?: string;
}

export function Post({
  post,
  onLike,
  onComment,
  onShare,
  onEdit,
  onDelete,
  onEditComment,
  onDeleteComment,
  onAddReply,
  onEditReply,
  onDeleteReply,
  onJoinEvent,
  onLeaveEvent,
  onCancelEvent,
  onEditEvent,
  currentUserId,
  showCommentsInitially = false,
  autoExpandCommentId,
  highlightReplyId,
}: PostProps) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { trackEvent } = usePosthog();
  const navigate = useNavigate();
  const userData = useSelector((state: AppState) => state.auth.userData);
  const user = userData?.user;
  const [showComments, setShowComments] = useState(showCommentsInitially);
  const [nestedComments, setNestedComments] = useState<any[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [localCommentsCount, setLocalCommentsCount] = useState(post.comments_count);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [editMediaUrls, setEditMediaUrls] = useState<string[]>(
    post["media_urls:raw"] || post.media_urls || [],
  );
  const [editTaggedUserIds, setEditTaggedUserIds] = useState<string[]>(() => {
    if (!post.tagged_users) return [];
    if (typeof post.tagged_users[0] === "string") return post.tagged_users as string[];
    return (post.tagged_users as any[]).map((user: any) => user.id);
  });
  const [editTaggedUserObjects, setEditTaggedUserObjects] = useState<SearchUser[]>(() => {
    if (!post.tagged_users) return [];
    if (typeof post.tagged_users[0] === "object") return post.tagged_users as SearchUser[];
    return [];
  });
  const [newImages, setNewImages] = useState<File[]>([]);
  const [newVideos, setNewVideos] = useState<File[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [showUnregisterDialog, setShowUnregisterDialog] = useState(false);
  const [showCancelEventDialog, setShowCancelEventDialog] = useState(false);
  const [pendingCancelEventId, setPendingCancelEventId] = useState<string | null>(null);
  const [showEditEmojiPicker, setShowEditEmojiPicker] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  // Initialize user's reaction from inline API data
  const [userReactionState, setUserReactionState] = useState<{ emoji: string } | null>(
    post.reaction ? { emoji: post.reaction.emoji } : null,
  );
  // Initialize reaction stats from inline API data
  const [localReactionStats, setLocalReactionStats] = useState<ReactionStat[]>(
    post.reaction_stats || [],
  );
  const [shareUsers, setShareUsers] = useState<ShareUser[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);
  const [showShareUsersDialog, setShowShareUsersDialog] = useState(false);
  const [reactionUsers, setReactionUsers] = useState<ReactionUser[]>([]);
  const [isLoadingReactions, setIsLoadingReactions] = useState(false);
  const [showReactionUsersDialog, setShowReactionUsersDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const postCardRef = useRef<HTMLDivElement>(null);
  const editTextareaRef = useRef<TiptapRef>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Carousel and video refs for pause functionality
  const [carouselApi, setCarouselApi] = useState<any>(null);
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());

  // Lightbox for viewing post images at full size
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null);

  // Event description editor states
  const [showEventEmojiPicker, setShowEventEmojiPicker] = useState(false);
  const editEventTextareaRef = useRef<TiptapRef>(null);

  // Event editing states
  const [editEventTitle, setEditEventTitle] = useState(post.eventDetails?.title || "");
  const [editEventDate, setEditEventDate] = useState<Date | undefined>(post.eventDetails?.date);
  const [editEventTime, setEditEventTime] = useState(
    post.eventDetails?.date
      ? `${String(post.eventDetails.date.getHours()).padStart(2, "0")}:${String(post.eventDetails.date.getMinutes()).padStart(2, "0")}`
      : "",
  );
  const [editEventEndTime, setEditEventEndTime] = useState(
    post.eventDetails?.endDate
      ? `${String(post.eventDetails.endDate.getHours()).padStart(2, "0")}:${String(post.eventDetails.endDate.getMinutes()).padStart(2, "0")}`
      : "",
  );
  const [editEventLocation, setEditEventLocation] = useState(post.eventDetails?.location || "");
  const [editEventMaxParticipants, setEditEventMaxParticipants] = useState(
    post.eventDetails?.maxParticipants?.toString() || "",
  );
  const [editEventMaxParticipantsError, setEditEventMaxParticipantsError] = useState("");
  const [editEventTimeError, setEditEventTimeError] = useState("");
  const [editEventDescription, setEditEventDescription] = useState(
    post.eventDetails?.description || "",
  );

  const isOwnPost = currentUserId === post.author?.id;

  // Event fields are editable only on an own, non-shared, non-cancelled event post.
  // Cancelled events stay locked (matching EventsPage/EventDetails), so editing a
  // cancelled post touches caption/media only and never re-sends event_status —
  // otherwise saving would silently revive the event.
  const canEditEventDetails = !!(
    post.isEvent &&
    !post.shared_post &&
    post.eventStatus !== "cancelled"
  );

  // Handle user profile click - navigate to their profile
  const handleUserProfileClick = (userId: string, userType: string, organizationId?: string) => {
    const currentUserType = user?.type;

    // Determine the navigation path based on user types
    if (currentUserType === "job_seeker") {
      if (userType === "job_seeker") {
        // Jobseeker viewing jobseeker - use userId
        navigate(`/jobseeker/users/${userId}`);
      } else if (userType === "employer") {
        // Jobseeker viewing employer - use organization_id if available
        if (organizationId) {
          navigate(`/jobseeker/employers/${organizationId}`);
        }
      } else {
        // If type is not available, default to jobseeker profile
        navigate(`/jobseeker/employers/${userId}`);
      }
    } else if (currentUserType === "employer") {
      if (userType === "employer") {
        // Employer viewing organization - use organization_id if available
        if (organizationId) {
          navigate(`/employer/organizations/${organizationId}`);
        }
      } else if (userType === "job_seeker") {
        // Employer viewing job seeker - use userId
        navigate(`/employer/jobseekers/${userId}`);
      } else {
        // If type is not available, default to organization profile
        navigate(`/employer/organizations/${userId}`);
      }
    }
  };

  // Handle tagged user click - navigate to their profile
  const handleTaggedUserClick = (taggedUser: any, event?: React.MouseEvent) => {
    // Stop event propagation to prevent triggering parent click handlers
    event?.stopPropagation();

    const taggedUserId = typeof taggedUser === "string" ? taggedUser : taggedUser.id;
    const taggedUserType = typeof taggedUser === "object" ? taggedUser.type : undefined;
    const organizationId = typeof taggedUser === "object" ? taggedUser.organization_id : undefined;

    if (taggedUserId && taggedUserType) {
      handleUserProfileClick(taggedUserId, taggedUserType, organizationId);
    } else if (taggedUserId) {
      // If we don't have the type, try to infer from the current user's perspective
      // Default to employer for job seekers, and jobseeker for employers
      const currentUserType = user?.type;
      if (currentUserType === "job_seeker") {
        // Job seeker likely tags employers/organizations
        navigate(`/jobseeker/employers/${taggedUserId}`);
      } else if (currentUserType === "employer") {
        // Employer likely tags job seekers
        navigate(`/employer/jobseekers/${taggedUserId}`);
      }
    }
  };

  // Update edit state when post prop changes (e.g., after successful edit)
  useEffect(() => {
    if (!isEditing) {
      setEditContent(post.content);
      setEditMediaUrls(post["media_urls:raw"] || post.media_urls || []);
      setEditTaggedUserIds(() => {
        if (!post.tagged_users) return [];
        if (typeof post.tagged_users[0] === "string") return post.tagged_users as string[];
        return (post.tagged_users as any[]).map((user: any) => user.id);
      });
      setEditTaggedUserObjects(() => {
        if (!post.tagged_users) return [];
        if (typeof post.tagged_users[0] === "object") return post.tagged_users as SearchUser[];
        return [];
      });
    }
  }, [post.content, post.media_urls, post["media_urls:raw"], post.tagged_users, isEditing]);

  // Function to fetch and transform top-level comments (replies fetched separately by CommentSection)
  const fetchNestedComments = async (showLoader = true) => {
    if (showLoader) {
      setIsLoadingComments(true);
    }
    try {
      const commentsData = await getCommentsByPost(post.id, 1, 8);

      // Transform API comments to UI format (without nested replies)
      const transformedComments = commentsData.comments.map((comment: any) => ({
        id: comment._id || comment.id,
        author: {
          id: comment.author?.id || comment.author_id,
          name: comment.author?.full_name || t("community.post.unknown_comment_author"),
          avatar: comment.author?.photo_thumbnail_url || comment.author?.photo,
          title:
            comment.author?.job_title ||
            (comment.author?.type === "employer"
              ? t("community.post.employer_fallback")
              : t("community.post.job_seeker_fallback")),
          type: comment.author?.type,
          organizationId: comment.author?.organization_id,
        },
        content: comment.content,
        timestamp: new Date(comment.created_at),
        isEdited: comment.updated_at !== comment.created_at,
        editedAt:
          comment.updated_at !== comment.created_at ? new Date(comment.updated_at) : undefined,
        likes: [],
        replies: [], // Replies will be fetched separately when user expands the comment
        replies_count: comment.replies_count || 0, // Store reply count for UI
      }));

      setNestedComments(transformedComments);
      setCommentsPage(1);
      setHasMoreComments(commentsData.pagination.page < commentsData.pagination.pages);
      setIsInitialLoad(false);
    } catch (error) {
      console.error("Error fetching comments:", error);
      toast({
        title: t("community.post.failed_to_load_comments"),
        description: t("community.post.failed_to_load_comments_desc"),
        variant: "destructive",
      });
    } finally {
      if (showLoader) {
        setIsLoadingComments(false);
      }
    }
  };

  // Function to load more comments
  const loadMoreComments = async () => {
    if (!hasMoreComments || isLoadingMoreComments) return;

    setIsLoadingMoreComments(true);
    try {
      const nextPage = commentsPage + 1;
      const commentsData = await getCommentsByPost(post.id, nextPage, 8);
      // Transform API comments to UI format
      const transformedComments = commentsData.comments.map((comment: any) => ({
        id: comment?._id || comment?.id,
        author: {
          id: comment?.author?.id || comment.author_id,
          name: comment?.author?.full_name || t("community.post.unknown"),
          avatar: comment?.author?.photo_thumbnail_url || comment.author?.photo,
          title:
            comment?.author?.job_title ||
            (comment?.author?.type === "employer"
              ? t("community.post.employer")
              : t("community.post.job_seeker")),
          type: comment?.author?.type,
          organizationId: comment?.author?.organization_id,
        },
        content: comment?.content,
        timestamp: new Date(comment?.created_at),
        isEdited: comment?.updated_at !== comment?.created_at,
        editedAt:
          comment?.updated_at !== comment?.created_at ? new Date(comment?.updated_at) : undefined,
        likes: [],
        replies: [],
        replies_count: comment?.replies_count || 0,
      }));

      // Append new comments to existing ones
      setNestedComments((prev) => [...prev, ...transformedComments]);
      setCommentsPage(nextPage);
      setHasMoreComments(commentsData?.pagination?.page < commentsData?.pagination?.pages);
    } catch (error) {
      console.error("Error loading more comments:", error);
      toast({
        title: t("community.post.failed_to_load_more_comments"),
        description: t("community.post.failed_to_load_comments_desc"),
        variant: "destructive",
      });
    } finally {
      setIsLoadingMoreComments(false);
    }
  };

  // Fetch nested comments when showComments is toggled
  useEffect(() => {
    if (showComments && isInitialLoad) {
      fetchNestedComments(true);
    }
  }, [showComments, post.id]);

  // Reactions are now loaded inline from the API response (post.reaction and post.reaction_stats)
  // No need to fetch reactions separately on initial load

  // Fetch shares when dialog opens
  const handleOpenShareUsersDialog = async () => {
    setShowShareUsersDialog(true);

    // Only fetch if we haven't fetched yet or if we need to refresh
    if (shareUsers.length === 0 && post.shares_count > 0) {
      setIsLoadingShares(true);
      try {
        const result = await getPostShares(post.id, 1, 50);
        setShareUsers(result.shares as ShareUser[]);
      } catch (error) {
        console.error("Error fetching shares:", error);
      } finally {
        setIsLoadingShares(false);
      }
    }
  };

  const handleOpenReactionUsersDialog = async () => {
    setShowReactionUsersDialog(true);

    // Only fetch if we haven't fetched yet or if we need to refresh
    if (reactionUsers.length === 0 && post.likes_count > 0) {
      setIsLoadingReactions(true);
      try {
        const result = await getPostReactions(post.id, 1, 50);
        setReactionUsers(result.reactions as ReactionUser[]);
      } catch (error) {
        console.error("Error fetching reactions:", error);
      } finally {
        setIsLoadingReactions(false);
      }
    }
  };

  useEffect(() => {
    if (showCommentsInitially && localCommentsCount > 0 && isInitialLoad) {
      fetchNestedComments(true);
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCommentsInitially, localCommentsCount]);

  // Pause all videos when carousel slide changes
  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      // Pause all videos when navigating to a different slide
      videoRefs.current.forEach((video) => {
        if (video && !video.paused) {
          video.pause();
        }
      });
    };

    carouselApi.on("select", onSelect);

    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  // Wrapper handlers that refetch comments after operations (without showing loader)
  const handleCommentWithRefresh = async (
    postId: string,
    comment: string,
    mentionedUserIds?: string[],
  ) => {
    await onComment?.(postId, comment, mentionedUserIds);
    await fetchNestedComments(false); // Refetch without loader
  };

  const handleEditCommentWithRefresh = async (
    postId: string,
    commentId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => {
    await onEditComment?.(postId, commentId, newContent, mentionedUserIds);
    await fetchNestedComments(false); // Refetch without loader
  };

  const handleDeleteCommentWithRefresh = async (postId: string, commentId: string) => {
    await onDeleteComment?.(postId, commentId);
    // Decrement local comment count
    setLocalCommentsCount((prev) => Math.max(0, prev - 1));
    await fetchNestedComments(false); // Refetch without loader
  };

  const handleAddReplyWithRefresh = async (
    postId: string,
    commentId: string,
    reply: string,
    mentionedUserIds?: string[],
  ) => {
    // CommentSection handles reply creation and state update directly
    // Just call parent handler for any additional updates
    onAddReply?.(postId, commentId, reply);
    // Increment local comment count
    setLocalCommentsCount((prev) => prev + 1);
  };

  const handleEditReplyWithRefresh = async (
    postId: string,
    commentId: string,
    replyId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => {
    await onEditReply?.(postId, commentId, replyId, newContent, mentionedUserIds);
    await fetchNestedComments(false); // Refetch without loader
  };

  const handleDeleteReplyWithRefresh = async (
    postId: string,
    commentId: string,
    replyId: string,
  ) => {
    await onDeleteReply?.(postId, commentId, replyId);
    // Decrement local comment count
    setLocalCommentsCount((prev) => Math.max(0, prev - 1));
    await fetchNestedComments(false); // Refetch without loader
  };

  // Get translated emoji labels - MUST BE DEFINED FIRST
  const reactions = POST_REACTIONS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));

  // Find user's reaction from state (initialized from post.reaction)
  const userReactionEmoji = userReactionState
    ? reactions.find((r) => r.emoji === userReactionState.emoji)
    : null;

  // Get translated construction emoji labels
  const constructionEmojis = CONSTRUCTION_EMOJIS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));

  // Get translated common emoji labels
  const commonEmojis = COMMON_EMOJIS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));

  const addEmojiToEdit = (emoji: string) => {
    if (editTextareaRef.current?.editor) {
      editTextareaRef.current.editor.commands.insertContent(emoji);
      editTextareaRef.current.editor.commands.focus();
    }
    setShowEditEmojiPicker(false);
  };

  const addEmojiToEventEdit = (emoji: string) => {
    if (editEventTextareaRef.current?.editor) {
      editEventTextareaRef.current.editor.commands.insertContent(emoji);
      editEventTextareaRef.current.editor.commands.focus();
    }
    setShowEventEmojiPicker(false);
  };

  const handleReaction = async (emoji: string) => {
    if (!currentUserId) return;

    setShowReactionPicker(false);

    // Check if user is removing their reaction (clicking same emoji)
    const isRemoving = userReactionState?.emoji === emoji;
    const previousEmoji = userReactionState?.emoji;

    // Optimistically update user's reaction state
    if (isRemoving) {
      setUserReactionState(null);
    } else {
      setUserReactionState({ emoji });
    }

    // Optimistically update reaction stats
    setLocalReactionStats((prevStats) => {
      let newStats = [...prevStats];

      // If user had a previous reaction, decrement its count
      if (previousEmoji) {
        newStats = newStats
          .map((stat) =>
            stat.emoji === previousEmoji ? { ...stat, count: Math.max(0, stat.count - 1) } : stat,
          )
          .filter((stat) => stat.count > 0);
      }

      // If not removing, add/increment the new emoji
      if (!isRemoving) {
        const existingStat = newStats.find((stat) => stat.emoji === emoji);
        if (existingStat) {
          newStats = newStats.map((stat) =>
            stat.emoji === emoji ? { ...stat, count: stat.count + 1 } : stat,
          );
        } else {
          newStats.push({ emoji, count: 1 });
        }
      }

      return newStats;
    });

    // Track reaction event
    trackEvent("community_post_reaction", {
      post_id: post.id,
      emoji,
      action: isRemoving ? "remove" : "add",
      post_type: post.isEvent ? "event" : "regular",
      is_own_post: isOwnPost,
    });

    // Call parent handler which updates backend
    try {
      await onLike?.(post.id, emoji);
    } catch (error) {
      console.error("Error updating reaction:", error);
      // On error, revert to original state from post data
      setUserReactionState(post.reaction ? { emoji: post.reaction.emoji } : null);
      setLocalReactionStats(post.reaction_stats || []);
    }
  };

  // Media management handlers
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewImages((prev) => [...prev, ...files]);
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewVideos((prev) => [...prev, ...files]);
  };

  const removeExistingMedia = (index: number) => {
    setEditMediaUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index: number) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewVideo = (index: number) => {
    setNewVideos((prev) => prev.filter((_, i) => i !== index));
  };

  // User tagging handlers
  const handleUserSearch = async (query: string) => {
    setUserSearchQuery(query);
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchUsers(query);
      setSearchResults(results.slice(0, 10));
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const addTaggedUser = (user: SearchUser) => {
    if (!editTaggedUserIds.includes(user.id)) {
      setEditTaggedUserIds((prev) => [...prev, user.id]);
      setEditTaggedUserObjects((prev) => [...prev, user]);
    }
    setUserSearchQuery("");
    setSearchResults([]);
    setShowUserSearch(false);
  };

  const removeTaggedUser = (userId: string) => {
    setEditTaggedUserIds((prev) => prev.filter((id) => id !== userId));
    setEditTaggedUserObjects((prev) => prev.filter((user) => user.id !== userId));
  };

  const getUserInitials = (name?: string) => {
    if (!name) return "U";
    const nameParts = name.trim().split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return nameParts[0][0].toUpperCase();
  };

  const handleSaveEdit = async () => {
    // if (!editContent.trim()) return;

    const isEventEdit = canEditEventDetails;

    if (isEventEdit) {
      // Required fields. The unified edit surface puts Save well below these
      // fields, so a silent return would read as a broken button.
      if (!editEventTitle.trim() || !editEventDate || !editEventTime || !editEventLocation.trim()) {
        toast({
          title: t("community.events.missing_fields"),
          description: t("community.events.missing_fields_desc"),
          variant: "destructive",
        });
        return;
      }

      // Validate max participants if provided
      if (
        editEventMaxParticipants &&
        (parseInt(editEventMaxParticipants) > 10000 || parseInt(editEventMaxParticipants) < 1)
      ) {
        toast({
          title: t("community.post.invalid_max_participants_title"),
          description: t("community.post.invalid_max_participants_desc"),
          variant: "destructive",
        });
        return;
      }

      // Validate event times
      if (editEventEndTime && !validateEditEventTimes(editEventTime, editEventEndTime)) {
        toast({
          title: t("community.post.invalid_event_times_title"),
          description: t("community.post.invalid_event_times_desc"),
          variant: "destructive",
        });
        return;
      }
    }

    setIsUploading(true);

    try {
      // Upload new images
      const newImageUrls: string[] = [];
      for (const image of newImages) {
        try {
          const uploadResponse = await uploadFileToServer({ file: image });
          if (uploadResponse?.path) {
            newImageUrls.push(uploadResponse.path);
          }
        } catch (error) {
          console.error("Error uploading image:", error);
          toast({
            title: t("community.post.failed_upload_image"),
            variant: "destructive",
          });
        }
      }

      // Upload new videos
      const newVideoUrls: string[] = [];
      for (const video of newVideos) {
        try {
          const uploadResponse = await uploadFileToServer({ file: video });
          if (uploadResponse?.path) {
            newVideoUrls.push(uploadResponse.path);
          }
        } catch (error) {
          console.error("Error uploading video:", error);
          toast({
            title: t("community.post.failed_upload_video"),
            variant: "destructive",
          });
        }
      }

      // Combine existing media URLs with new uploads
      const allMediaUrls = [...editMediaUrls, ...newImageUrls, ...newVideoUrls];

      // Extract mentioned user IDs from content
      const extractMentionedUserIds = (htmlContent: string): string[] => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        const mentionElements = doc.querySelectorAll('[data-type="mention"]');
        const userIds: string[] = [];

        mentionElements.forEach((element) => {
          const userId = element.getAttribute("data-id");
          if (userId && !userIds.includes(userId)) {
            userIds.push(userId);
          }
        });

        return userIds;
      };

      const mentionedUserIds = extractMentionedUserIds(editContent);
      const allTaggedUserIds = Array.from(new Set([...mentionedUserIds, ...editTaggedUserIds]));

      // Track post edit event
      trackEvent("community_post_edited", {
        post_id: post.id,
        post_type: post.isEvent ? "event" : "regular",
        content_changed: editContent !== post.content,
        media_added: newImages.length + newVideos.length > 0,
        media_removed: editMediaUrls.length < (post.media_urls?.length || 0),
        new_media_count: newImages.length + newVideos.length,
        total_media_count: allMediaUrls.length,
      });

      // Build event date/time objects when editing an event post
      let eventDateTime: Date | undefined;
      let eventEndDateTime: Date | undefined;
      if (isEventEdit) {
        const [hours, minutes] = editEventTime.split(":");
        eventDateTime = new Date(editEventDate);
        eventDateTime.setHours(parseInt(hours), parseInt(minutes));

        if (editEventEndTime) {
          const [endHours, endMinutes] = editEventEndTime.split(":");
          eventEndDateTime = new Date(editEventDate);
          eventEndDateTime.setHours(parseInt(endHours), parseInt(endMinutes));
        }
      }

      // Awaited so a failed save keeps the edit surface open with the user's
      // changes intact. Without this the surface closed immediately and a
      // rejected request left the card showing stale data with no feedback.
      await onEdit?.(post.id, {
        content: editContent,
        media_urls: allMediaUrls,
        tagged_users: allTaggedUserIds,
        ...(isEventEdit && {
          eventDetails: {
            title: editEventTitle,
            date: eventDateTime,
            endDate: eventEndDateTime,
            location: editEventLocation,
            maxParticipants: editEventMaxParticipants
              ? parseInt(editEventMaxParticipants)
              : undefined,
            description: editEventDescription || undefined,
          },
        }),
      });

      // Reset new files
      setNewImages([]);
      setNewVideos([]);
      setIsEditing(false);
    } catch (error) {
      // Deliberately no toast here: the parent's handleEdit already surfaces the
      // failure (with the server's message) before rethrowing, so toasting again
      // would double up. We just keep the surface open so the edits aren't lost.
      console.error("Error saving edit:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(post.content);
    setEditMediaUrls(post["media_urls:raw"] || post.media_urls || []);
    setEditTaggedUserIds(() => {
      if (!post.tagged_users) return [];
      if (typeof post.tagged_users[0] === "string") return post.tagged_users as string[];
      return (post.tagged_users as any[]).map((user: any) => user.id);
    });
    setEditTaggedUserObjects(() => {
      if (!post.tagged_users) return [];
      if (typeof post.tagged_users[0] === "object") return post.tagged_users as SearchUser[];
      return [];
    });
    setNewImages([]);
    setNewVideos([]);
    setUserSearchQuery("");
    setSearchResults([]);
    setShowUserSearch(false);
    // Reset event fields to original values
    setEditEventTitle(post.eventDetails?.title || "");
    setEditEventDate(post.eventDetails?.date);
    setEditEventTime(
      post.eventDetails?.date
        ? `${String(post.eventDetails.date.getHours()).padStart(2, "0")}:${String(post.eventDetails.date.getMinutes()).padStart(2, "0")}`
        : "",
    );
    setEditEventEndTime(
      post.eventDetails?.endDate
        ? `${String(post.eventDetails.endDate.getHours()).padStart(2, "0")}:${String(post.eventDetails.endDate.getMinutes()).padStart(2, "0")}`
        : "",
    );
    setEditEventLocation(post.eventDetails?.location || "");
    setEditEventMaxParticipants(post.eventDetails?.maxParticipants?.toString() || "");
    setEditEventDescription(post.eventDetails?.description || "");
    // Clear validation errors too, otherwise reopening Edit shows a red border and
    // a stale message against the restored (valid) values.
    setEditEventMaxParticipantsError("");
    setEditEventTimeError("");
    setIsEditing(false);
  };

  const handleDelete = () => {
    trackEvent("community_post_deleted", {
      post_id: post.id,
      post_type: post.isEvent ? "event" : "regular",
      has_reason: !!deleteReason,
      has_media: (post.media_urls?.length || 0) > 0,
      has_comments: localCommentsCount > 0,
      has_likes: post.likes_count > 0,
    });
    // Pass post type information to parent handler
    onDelete?.(post.id, deleteReason || undefined, post.type, post.isEvent);
    setShowDeleteDialog(false);
    setDeleteReason("");
  };

  const handleUnregisterEvent = () => {
    // Use the original event ID for shared events
    const eventId = post.shared_post?.isEvent ? post.shared_post.id : post.id;

    trackEvent("community_event_left", {
      post_id: eventId,
      participant_count:
        (post.shared_post?.eventParticipants || post.eventParticipants)?.length || 0,
      is_shared_event: !!post.shared_post?.isEvent,
    });
    onLeaveEvent?.(eventId);
    setShowUnregisterDialog(false);
  };

  const handleShareToFeed = () => {
    trackEvent("community_post_shared", {
      post_id: post.id,
      post_type: post.isEvent ? "event" : "regular",
      share_method: "feed",
    });

    onShare?.(post.id);
    setShowShareMenu(false);
  };

  const handleShareToLinkedIn = () => {
    trackEvent("community_post_shared", {
      post_id: post.id,
      post_type: post.isEvent ? "event" : "regular",
      share_method: "linkedin",
    });

    // Construct URL with just the base domain and /postId
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const postUrl = `${baseUrl}/community/post/${post.id}`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`;
    window.open(linkedInUrl, "_blank", "noopener,noreferrer");
    setShowShareMenu(false);
  };

  const handleShareToWhatsApp = () => {
    trackEvent("community_post_shared", {
      post_id: post.id,
      post_type: post.isEvent ? "event" : "regular",
      share_method: "whatsapp",
    });

    // Construct URL with just the base domain and /postId
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const postUrl = `${baseUrl}/community/post/${post.id}`;
    const text = `Check out this post:`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + "\n" + postUrl)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    setShowShareMenu(false);
  };

  const handleEditMaxParticipantsChange = (value: string) => {
    setEditEventMaxParticipants(value);

    // Validate max participants
    if (value && parseInt(value) > 10000) {
      setEditEventMaxParticipantsError(t("community.post.max_participants_error"));
    } else if (value && parseInt(value) < 1) {
      setEditEventMaxParticipantsError(t("community.post.min_participants_error"));
    } else {
      setEditEventMaxParticipantsError("");
    }
  };

  const validateEditEventTimes = (startTime: string, endTime: string) => {
    if (startTime && endTime) {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      const startInMinutes = startHours * 60 + startMinutes;
      const endInMinutes = endHours * 60 + endMinutes;

      if (startInMinutes >= endInMinutes) {
        setEditEventTimeError(t("community.post.end_time_after_start_error"));
        return false;
      } else {
        setEditEventTimeError("");
        return true;
      }
    }
    setEditEventTimeError("");
    return true;
  };

  const handleEditEventTimeChange = (value: string) => {
    setEditEventTime(value);
    validateEditEventTimes(value, editEventEndTime);
  };

  const handleEditEventEndTimeChange = (value: string) => {
    setEditEventEndTime(value);
    validateEditEventTimes(editEventTime, value);
  };

  // Use reaction stats from API (converted to Record for easier access)
  const groupedReactions = (localReactionStats || []).reduce(
    (acc, stat) => {
      acc[stat.emoji] = stat.count;
      return acc;
    },
    {} as Record<string, number>,
  );

  const hasMedia = post.media_urls && post.media_urls.length > 0;

  return (
    <TooltipProvider>
      <Card
        ref={postCardRef}
        className="border-border transition-all hover:-translate-y-0.5 hover:shadow-md"
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex gap-3">
              <Avatar
                className="ring-border hover:ring-primary/50 h-12 w-12 cursor-pointer ring-2 transition transition-opacity hover:opacity-80"
                onClick={() =>
                  post.author?.id &&
                  post.author?.type &&
                  handleUserProfileClick(
                    post.author.id,
                    post.author.type,
                    post.author.organization_id,
                  )
                }
              >
                <AvatarImage src={post.author?.photo_thumbnail_url || post.author?.photo} />
                <AvatarFallback className="bg-brand-accent text-primary font-semibold">
                  {(post.author?.full_name || "U")
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3
                  className="cursor-pointer text-base font-semibold hover:underline"
                  onClick={() =>
                    post.author?.id &&
                    post.author?.type &&
                    handleUserProfileClick(
                      post.author.id,
                      post.author.type,
                      post.author.organization_id,
                    )
                  }
                >
                  {post.author?.full_name}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-muted-foreground text-sm">
                    {post?.author?.job_title ||
                      (post?.author?.type === "employer"
                        ? t("community.post.employer_fallback")
                        : t("community.post.job_seeker_fallback"))}
                  </p>
                  <span className="text-muted-foreground text-xs">•</span>
                  <p className="text-muted-foreground text-sm">{formatTimestamp(post.timestamp)}</p>
                  {post.isEdited && (
                    <>
                      <span className="text-muted-foreground text-xs">•</span>
                      <span className="text-muted-foreground text-xs italic">
                        {t("community.post.edited")}
                      </span>
                    </>
                  )}
                </div>
                {post.shared_post && (
                  <div className="mt-2 flex items-start gap-3">
                    <div className="flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="secondary"
                          className="border-brand-accent-border bg-brand-accent hover:bg-brand-accent text-brand-accent-foreground gap-1 text-xs"
                        >
                          <Share2 className="h-3 w-3" />
                          {t("community.post.shared_to_feed_badge")}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {t("community.post.originally_by")} {post.shared_post.full_name}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-muted">
                  <MoreHorizontal className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-border rounded-xl p-1.5 shadow-lg">
                {isOwnPost && (
                  <>
                    {!post.shared_post && (
                      <DropdownMenuItem
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium"
                      >
                        <Edit2 className="h-4 w-4" />
                        {t("community.post.edit_post")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("community.post.delete_post")}
                    </DropdownMenuItem>
                  </>
                )}
                {!isOwnPost && (
                  <DropdownMenuItem
                    onClick={() => setShowReportDialog(true)}
                    className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-amber-700 focus:bg-amber-50 focus:text-amber-700"
                  >
                    <Flag className="h-4 w-4" />
                    {t("community.report.report")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        <CardContent className="space-y-5 pt-0">
          {/* Post Content or Edit Mode */}
          {isEditing ? (
            <div className="space-y-3">
              <h4 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                {t("community.post.edit_post")}
              </h4>
              <Tiptap
                ref={editTextareaRef}
                content={editContent}
                onChange={setEditContent}
                placeholder={t("community.post.edit_post_placeholder")}
                enableMention={true}
                enableLink={true}
                enableAutolink={true}
                disabled={isUploading}
              />

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Emoji Picker */}
                <Popover open={showEditEmojiPicker} onOpenChange={setShowEditEmojiPicker}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="hover:bg-primary/10 hover:text-primary gap-2"
                      disabled={isUploading}
                    >
                      <Smile className="h-4 w-4" />
                      <span className="text-xs">{t("community.post.emoji")}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 rounded-xl p-3 shadow-lg" align="start">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                          🏗️ {t("community.create_post.construction_emojis")}
                        </h4>
                        <div className="grid grid-cols-8 gap-1">
                          {constructionEmojis.map((item, index) => (
                            <button
                              key={index}
                              onClick={() => addEmojiToEdit(item.emoji)}
                              className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                              type="button"
                              disabled={isUploading}
                            >
                              {item.emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                          😊 {t("community.create_post.common")}
                        </h4>
                        <div className="grid grid-cols-8 gap-1">
                          {commonEmojis.map((item, index) => (
                            <button
                              key={index}
                              onClick={() => addEmojiToEdit(item.emoji)}
                              className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                              type="button"
                              disabled={isUploading}
                            >
                              {item.emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Add Images */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="hover:bg-primary/10 hover:text-primary gap-2"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <ImageIcon className="h-4 w-4" />
                  <span className="text-xs">{t("community.post.image")}</span>
                </Button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                  disabled={isUploading}
                />

                {/* Add Videos */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="hover:bg-primary/10 hover:text-primary gap-2"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <VideoIcon className="h-4 w-4" />
                  <span className="text-xs">{t("community.post.video")}</span>
                </Button>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={handleVideoSelect}
                  disabled={isUploading}
                />

                {/* Tag User */}
                <Popover open={showUserSearch} onOpenChange={setShowUserSearch}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="hover:bg-primary/10 hover:text-primary gap-2"
                      disabled={isUploading}
                    >
                      <Tag className="h-4 w-4" />
                      <span className="text-xs">{t("community.post.tag_user")}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 rounded-xl p-0 shadow-lg" align="start">
                    <div className="p-3">
                      <Input
                        placeholder={t("community.post.search_users_placeholder")}
                        value={userSearchQuery}
                        onChange={(e) => handleUserSearch(e.target.value)}
                        className="mb-2 h-9 text-sm"
                        disabled={isUploading}
                      />
                      <Command>
                        <CommandList className="max-h-60">
                          {isSearching ? (
                            <div className="py-6 text-center text-sm">
                              {t("community.post.searching")}
                            </div>
                          ) : searchResults.length > 0 ? (
                            <CommandGroup>
                              {searchResults.map((user) => (
                                <CommandItem
                                  key={user.id}
                                  onSelect={() => addTaggedUser(user)}
                                  className="cursor-pointer gap-3 rounded-lg px-2 py-2"
                                >
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-6 w-6">
                                      <AvatarImage src={user.photo_thumbnail_url || ""} />
                                      <AvatarFallback className="text-xs">
                                        {getUserInitials(user.full_name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                      <div className="text-sm font-medium">{user.full_name}</div>
                                      <div className="text-muted-foreground text-xs">
                                        {user.email}
                                      </div>
                                    </div>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          ) : userSearchQuery.length >= 2 ? (
                            <CommandEmpty>{t("community.post.no_users_found")}</CommandEmpty>
                          ) : (
                            <div className="text-muted-foreground py-6 text-center text-sm">
                              {t("community.create_post.type_to_search")}
                            </div>
                          )}
                        </CommandList>
                      </Command>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Display Existing Media */}
              {(editMediaUrls.length > 0 || newImages.length > 0 || newVideos.length > 0) && (
                <div
                  className={`grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 ${isUploading ? "pointer-events-none opacity-60" : ""}`}
                >
                  {/* Existing Media */}
                  {editMediaUrls.map((rawUrl, index) => {
                    // Map raw URL to signed URL for preview
                    // If we have media_urls:raw, find the corresponding signed URL from media_urls
                    const rawUrls = post["media_urls:raw"] || [];
                    const signedUrls = post.media_urls || [];
                    let displayUrl = rawUrl;

                    // If we're using raw URLs, map to signed URL for display
                    if (rawUrls.length > 0) {
                      const rawIndex = rawUrls.indexOf(rawUrl);
                      if (rawIndex !== -1 && rawIndex < signedUrls.length) {
                        displayUrl = signedUrls[rawIndex];
                      }
                    }

                    const mediaItem = mapMediaUrls([displayUrl])[0];
                    return (
                      <div key={`existing-${index}`} className="group relative">
                        {mediaItem.type === "video" ? (
                          <video
                            src={mediaItem.url}
                            className="h-32 w-full rounded-md object-cover"
                          />
                        ) : (
                          <img
                            src={mediaItem.url}
                            alt={`Media ${index + 1}`}
                            loading="lazy"
                            className="h-32 w-full rounded-md object-cover"
                          />
                        )}
                        <Button
                          type="button"
                          size="icon"
                          variant="destructive"
                          className="absolute top-1 right-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                          onClick={() => removeExistingMedia(index)}
                          disabled={isUploading}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}

                  {/* New Images */}
                  {newImages.map((file, index) => (
                    <div key={`new-img-${index}`} className="group relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`New image ${index + 1}`}
                        loading="lazy"
                        className="h-32 w-full rounded-md object-cover"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        className="absolute top-1 right-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={() => removeNewImage(index)}
                        disabled={isUploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  {/* New Videos */}
                  {newVideos.map((file, index) => (
                    <div key={`new-vid-${index}`} className="group relative">
                      <video
                        src={URL.createObjectURL(file)}
                        className="h-32 w-full rounded-md object-cover"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        className="absolute top-1 right-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={() => removeNewVideo(index)}
                        disabled={isUploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Display Tagged Users */}
              {editTaggedUserObjects.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editTaggedUserObjects.map((user) => (
                    <Badge
                      key={user.id}
                      variant="secondary"
                      className="border-brand-accent-border bg-brand-accent hover:bg-brand-accent text-brand-accent-foreground gap-1.5 pr-1.5 pl-1 font-medium"
                    >
                      <Avatar className="h-4 w-4">
                        <AvatarImage src={user.photo_thumbnail_url || ""} />
                        <AvatarFallback className="bg-card text-primary text-xs">
                          {getUserInitials(user.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs">{user.full_name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="hover:bg-primary/15 ml-0.5 h-4 w-4 rounded-full p-0.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => removeTaggedUser(user.id)}
                        disabled={isUploading}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}

              {canEditEventDetails && (
                <div className="border-brand-accent-border bg-brand-accent/30 space-y-3 rounded-xl border p-3">
                  <h4 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    {t("community.post.edit_event")}
                  </h4>
                  <div>
                    <label className="mb-1 block text-sm font-semibold">
                      {t("community.events.event_title")}{" "}
                      <span className="text-destructive">*</span>
                    </label>
                    <Input
                      type="text"
                      value={editEventTitle}
                      onChange={(e) => setEditEventTitle(e.target.value)}
                      placeholder={t("community.post.event_title_placeholder_example")}
                      className="w-full"
                      disabled={isUploading}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold">
                        {t("community.events.date")} <span className="text-destructive">*</span>
                      </label>
                      <ShadCNDatePicker
                        value={editEventDate}
                        onChange={setEditEventDate}
                        placeholder={t("community.post.pick_date")}
                        disablePastDates
                        disabled={isUploading}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-semibold">
                        {t("community.events.max_participants")}
                      </label>
                      <Input
                        type="number"
                        value={editEventMaxParticipants}
                        onChange={(e) => handleEditMaxParticipantsChange(e.target.value)}
                        placeholder={t("community.post.max_participants_placeholder")}
                        min="1"
                        max="10000"
                        className={`w-full ${editEventMaxParticipantsError ? "border-destructive" : ""}`}
                        disabled={isUploading}
                      />
                      {editEventMaxParticipantsError && (
                        <p className="text-destructive mt-1 text-xs">
                          {editEventMaxParticipantsError}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <TimeInput
                      id="edit-event-start-time"
                      label={t("community.post.start_time")}
                      value={editEventTime}
                      onChange={handleEditEventTimeChange}
                      required
                      disabled={isUploading}
                    />
                    <div>
                      <TimeInput
                        id="edit-event-end-time"
                        label={t("community.post.end_time")}
                        value={editEventEndTime}
                        onChange={handleEditEventEndTimeChange}
                        disabled={isUploading}
                      />
                      {editEventTimeError && (
                        <p className="text-destructive mt-1 text-xs">{editEventTimeError}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-semibold">
                      {t("community.events.location")} <span className="text-destructive">*</span>
                    </label>
                    <Input
                      type="text"
                      value={editEventLocation}
                      onChange={(e) => setEditEventLocation(e.target.value)}
                      placeholder={t("community.post.location_placeholder_example")}
                      className="w-full"
                      disabled={isUploading}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-semibold">
                      {t("community.events.description_optional")}
                    </label>
                    <Tiptap
                      ref={editEventTextareaRef}
                      content={editEventDescription}
                      onChange={setEditEventDescription}
                      placeholder={t("community.post.event_details_placeholder")}
                      enableLink={true}
                      enableAutolink={true}
                      disabled={isUploading}
                    />

                    {/* Emoji Picker */}
                    <div className="mt-2 flex items-center gap-1">
                      <Popover open={showEventEmojiPicker} onOpenChange={setShowEventEmojiPicker}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="hover:bg-primary/10 hover:text-primary gap-2"
                            disabled={isUploading}
                          >
                            <Smile className="h-4 w-4" />
                            <span className="text-xs">{t("community.post.add_emoji")}</span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 rounded-xl p-3 shadow-lg" align="start">
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                                🏗️ {t("community.create_post.construction_emojis")}
                              </h4>
                              <div className="grid grid-cols-8 gap-1">
                                {constructionEmojis.map((item, index) => (
                                  <button
                                    key={index}
                                    onClick={() => addEmojiToEventEdit(item.emoji)}
                                    className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                                    type="button"
                                    disabled={isUploading}
                                  >
                                    {item.emoji}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <Separator />
                            <div>
                              <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                                😊 {t("community.create_post.common")}
                              </h4>
                              <div className="grid grid-cols-8 gap-1">
                                {commonEmojis.map((item, index) => (
                                  <button
                                    key={index}
                                    onClick={() => addEmojiToEventEdit(item.emoji)}
                                    className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                                    type="button"
                                    disabled={isUploading}
                                  >
                                    {item.emoji}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  onClick={handleCancelEdit}
                  size="sm"
                  variant="ghost"
                  className="gap-2"
                  disabled={isUploading}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} size="sm" className="gap-2" disabled={isUploading}>
                  <Check className="h-4 w-4" />
                  {isUploading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Check if shared post is deleted or original post is not available */}
              {((post.type === "shared_post" || post.type === "shared") &&
                !post.shared_post &&
                !post.original_post) ||
              post.shared_post?.deleted ? (
                <Card className="border-muted bg-muted/20 border-2">
                  <CardContent className="p-6 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="bg-muted rounded-full p-3">
                        <Trash2 className="text-muted-foreground h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-foreground font-semibold">
                          {t("community.post.original_post_deleted")}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                          {t("community.post.post_deleted_by_author")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <MentionRenderer
                  content={
                    post.shared_post ? post.shared_post.content || post.content : post.content
                  }
                  className="text-foreground text-sm md:text-base"
                />
              )}
            </>
          )}

          {/* Tagged Users - Show shared post tagged users if it's a shared post (only if not deleted) */}
          {!isEditing &&
            post.shared_post &&
            !post.shared_post.deleted &&
            post.shared_post.tagged_users &&
            Array.isArray(post.shared_post.tagged_users) &&
            post.shared_post.tagged_users.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.shared_post.tagged_users.map((user: any, index: number) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="hover:bg-secondary/80 cursor-pointer text-xs transition-colors"
                    onClick={(e) => handleTaggedUserClick(user, e)}
                  >
                    @{typeof user === "string" ? user : user.full_name}
                  </Badge>
                ))}
              </div>
            )}

          {/* Tagged Users - Show regular post tagged users if it's not a shared post */}
          {!isEditing &&
            !post.shared_post &&
            post.tagged_users &&
            Array.isArray(post.tagged_users) &&
            post.tagged_users.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.tagged_users.map((user: any, index: number) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="hover:bg-secondary/80 cursor-pointer text-xs transition-colors"
                    onClick={(e) => handleTaggedUserClick(user, e)}
                  >
                    @{typeof user === "string" ? user : user.full_name}
                  </Badge>
                ))}
              </div>
            )}

          {/* Media - Images and Videos */}
          {!isEditing &&
            (hasMedia ||
              (post.shared_post &&
                !post.shared_post.deleted &&
                post.shared_post.media_urls &&
                post.shared_post.media_urls.length > 0)) && (
              <div className="relative">
                <Carousel className="w-full" setApi={setCarouselApi}>
                  <CarouselContent>
                    {mapMediaUrls(
                      post.shared_post ? post.shared_post.media_urls || [] : post.media_urls || [],
                    ).map((mediaItem, index) => (
                      <CarouselItem
                        key={`media-${index}`}
                        className="flex items-center justify-center"
                      >
                        {mediaItem.type === "video" ? (
                          <video
                            ref={(el) => {
                              if (el) {
                                videoRefs.current.set(index, el);
                              } else {
                                videoRefs.current.delete(index);
                              }
                            }}
                            controls
                            playsInline
                            preload="metadata"
                            className="max-h-[500px] w-full rounded-lg"
                          >
                            <source src={mediaItem.url} type="video/mp4" />
                            <source src={mediaItem.url} type="video/webm" />
                          </video>
                        ) : mediaItem.type === "image" ? (
                          <div className="relative inline-block max-w-full">
                            <img
                              src={mediaItem.url}
                              alt={`Post media ${index + 1}`}
                              loading="lazy"
                              onClick={() => setLightboxImageUrl(mediaItem.url)}
                              className="block max-h-[500px] w-auto max-w-full cursor-zoom-in rounded-lg object-contain"
                            />
                            <button
                              type="button"
                              onClick={() => setLightboxImageUrl(mediaItem.url)}
                              aria-label={t("community.post.enlarge_image")}
                              title={t("community.post.enlarge_image")}
                              className="absolute top-2 right-2 rounded-full bg-black/50 p-1.5 text-white opacity-90 transition hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                            >
                              <Maximize2 className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="bg-muted flex max-h-[500px] w-full items-center justify-center rounded-lg p-8">
                            <div className="space-y-2 text-center">
                              <p className="text-muted-foreground text-sm">
                                {t("community.post.unsupported_media_type")}
                              </p>
                              <a
                                href={mediaItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary text-sm hover:underline"
                              >
                                View file
                              </a>
                            </div>
                          </div>
                        )}
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {(post.shared_post
                    ? post.shared_post.media_urls?.length || 0
                    : post.media_urls?.length || 0) > 1 && (
                    <>
                      <CarouselPrevious className="left-2" />
                      <CarouselNext className="right-2" />
                    </>
                  )}
                </Carousel>
              </div>
            )}

          {/* Event Details - Show for both regular events and shared events (only if not deleted).
              Hidden while editing: the unified edit surface already renders the event fields. */}
          {!isEditing &&
            ((post.isEvent && post.eventDetails) ||
              (post.shared_post?.isEvent &&
                post.shared_post?.eventDetails &&
                !post.shared_post.deleted)) && (
              <Card
                className={`border ${(post.shared_post?.eventStatus || post.eventStatus) === "cancelled" ? "border-destructive/20 bg-destructive/5" : "border-brand-accent-border bg-brand-accent/30"}`}
              >
                {
                  // Event Display
                  (() => {
                    // Use shared post event details if available, otherwise use regular post event details
                    const eventDetails = post.shared_post?.eventDetails || post.eventDetails!;
                    const eventStatus = post.shared_post?.eventStatus || post.eventStatus;
                    const eventParticipants =
                      post.shared_post?.eventParticipants || post.eventParticipants;
                    const isSharedEvent = !!post.shared_post?.isEvent;
                    // Use the original event post ID for shared events (for join/leave/cancel operations)
                    const eventPostId = isSharedEvent ? post.shared_post?.id : post.id;

                    return (
                      <>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="flex min-w-0 items-start gap-2">
                                <Calendar className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                                <h3 className="text-xl leading-snug font-bold">
                                  {eventDetails.title}
                                </h3>
                              </div>
                              {post.isEdited && (
                                <Badge variant="secondary" className="gap-1 text-xs">
                                  {t("community.post.updated_badge")}
                                </Badge>
                              )}
                              {eventStatus === "cancelled" && (
                                <Badge variant="destructive" className="gap-1">
                                  <X className="h-3 w-3" />
                                  {t("community.post.cancelled_badge")}
                                </Badge>
                              )}
                            </div>
                            <Badge
                              variant="secondary"
                              className="border-brand-accent-border bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent gap-1"
                            >
                              <Users className="h-3 w-3" />
                              {eventParticipants?.length || 0}
                              {eventDetails.maxParticipants && `/${eventDetails.maxParticipants}`}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid gap-3">
                            <div className="flex items-center gap-3 text-sm">
                              <Clock className="text-muted-foreground h-4 w-4 shrink-0" />
                              <div>
                                <p className="font-medium">
                                  {eventDetails.date.toLocaleDateString("en-US", {
                                    weekday: "long",
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })}
                                </p>
                                <p className="text-muted-foreground">
                                  {eventDetails.date.toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                  {eventDetails.endDate && (
                                    <>
                                      {" "}
                                      -{" "}
                                      {eventDetails.endDate.toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3 text-sm">
                              <MapPin className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                              <p className="font-medium">{eventDetails.location}</p>
                            </div>
                            {eventDetails.description && (
                              <div
                                className="text-muted-foreground border-t pt-2 text-sm whitespace-break-spaces"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHtml(eventDetails.description),
                                }}
                              />
                            )}
                          </div>

                          {/* Event Action Buttons - Use original event ID for shared events */}
                          <div className="pt-3">
                            {eventStatus === "cancelled" ? (
                              <Button
                                variant="ghost"
                                className="border-border bg-muted text-muted-foreground hover:bg-muted w-full cursor-not-allowed border"
                                disabled
                              >
                                {t("community.post.event_cancelled_button")}
                              </Button>
                            ) : (() => {
                                // Check if current user is the original event creator
                                const isOriginalEventCreator = isSharedEvent
                                  ? post.shared_post?.author_id === currentUserId
                                  : isOwnPost;

                                return isOriginalEventCreator;
                              })() ? (
                              // Only original event creator can cancel
                              <Button
                                onClick={() => {
                                  setPendingCancelEventId(eventPostId || post.id);
                                  setShowCancelEventDialog(true);
                                }}
                                variant="destructive"
                                className="w-full gap-2"
                              >
                                <X className="h-4 w-4" />
                                {t("community.events.cancel_event")}
                              </Button>
                            ) : eventParticipants?.some((p) => p.userId === currentUserId) ? (
                              // Registered user can leave
                              <Button
                                onClick={() => setShowUnregisterDialog(true)}
                                variant="outline"
                                className="border-destructive text-destructive hover:bg-destructive/10 w-full gap-2"
                              >
                                <X className="h-4 w-4" />
                                {t("community.events.unregister_from_event")}
                              </Button>
                            ) : eventDetails.maxParticipants &&
                              (eventParticipants?.length || 0) >= eventDetails.maxParticipants ? (
                              <Button variant="outline" className="w-full" disabled>
                                {t("community.events.event_full")}
                              </Button>
                            ) : (
                              <Button
                                onClick={() => {
                                  trackEvent("community_event_joined", {
                                    post_id: eventPostId || post.id,
                                    participant_count: eventParticipants?.length || 0,
                                    max_participants: eventDetails?.maxParticipants,
                                    is_shared_event: isSharedEvent,
                                  });
                                  onJoinEvent?.(eventPostId || post.id);
                                }}
                                className="bg-primary hover:bg-primary/90 w-full gap-2"
                              >
                                <UserCheck className="h-4 w-4" />
                                {t("community.events.join")}
                              </Button>
                            )}
                          </div>

                          {/* Participants Preview */}
                          {eventParticipants && eventParticipants.length > 0 && (
                            <div className="border-t pt-3">
                              <p className="text-muted-foreground mb-2 text-xs font-semibold">
                                {t("community.events.participants_count", {
                                  count: eventParticipants.length,
                                })}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {eventParticipants.slice(0, 5).map((participant, index) => (
                                  <Tooltip key={index}>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        variant="secondary"
                                        className="hover:bg-secondary/80 cursor-pointer text-xs transition-colors"
                                        onClick={() =>
                                          participant.userId &&
                                          participant.userType &&
                                          handleUserProfileClick(
                                            participant.userId,
                                            participant.userType,
                                            participant.organizationId,
                                          )
                                        }
                                      >
                                        {participant.userName}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{participant.userTitle}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                                {eventParticipants.length > 5 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {t("community.events.more_participants", {
                                      count: eventParticipants.length - 5,
                                    })}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </>
                    );
                  })()
                }
              </Card>
            )}

          {/* Reaction Summary */}
          {(Object.keys(groupedReactions).length > 0 ||
            localCommentsCount > 0 ||
            post.shares_count > 0) && (
            <div className="flex flex-wrap items-center justify-between gap-y-1 px-2 text-sm">
              <div className="text-muted-foreground flex min-w-0 items-center gap-3">
                {Object.keys(groupedReactions).length > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={handleOpenReactionUsersDialog}
                        className="group/react border-border bg-muted/60 hover:border-brand-accent-border inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 transition-colors hover:bg-blue-50"
                      >
                        <span className="flex -space-x-1.5">
                          {Object.keys(groupedReactions)
                            .slice(0, 3)
                            .map((emoji, index) => (
                              <span
                                key={index}
                                className="bg-card ring-border flex h-5 w-5 items-center justify-center rounded-full text-[13px] ring-1"
                              >
                                {emoji}
                              </span>
                            ))}
                        </span>
                        <span className="text-muted-foreground group-hover/react:text-primary text-xs font-semibold">
                          {Object.values(groupedReactions).reduce((sum, count) => sum + count, 0)}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">{t("community.post.click_to_see_reactions")}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <div className="text-muted-foreground flex shrink-0 items-center gap-3">
                {localCommentsCount > 0 && (
                  <button
                    onClick={() => setShowComments(!showComments)}
                    className="hover:text-foreground transition-colors hover:underline"
                  >
                    {localCommentsCount}{" "}
                    {localCommentsCount === 1
                      ? t("community.post.comment_count")
                      : t("community.post.comments_count")}
                  </button>
                )}
                {post.shares_count > 0 && (
                  <span
                    onClick={handleOpenShareUsersDialog}
                    className="hover:text-foreground cursor-pointer transition-colors hover:underline"
                  >
                    {post.shares_count}{" "}
                    {post.shares_count === 1
                      ? t("community.post.share_count")
                      : t("community.post.shares_count")}
                  </span>
                )}
              </div>
            </div>
          )}

          <Separator />

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Popover open={showReactionPicker} onOpenChange={setShowReactionPicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className={`flex-1 gap-2 px-1 py-1 transition-all md:px-3 md:py-1.5 ${
                    userReactionEmoji
                      ? "text-primary hover:bg-primary/10 hover:text-primary"
                      : "hover:bg-primary/10 hover:text-primary"
                  }`}
                >
                  <>
                    {userReactionEmoji ? (
                      <span className="animate-in zoom-in text-xl duration-300">
                        {userReactionEmoji.emoji}
                      </span>
                    ) : (
                      <ThumbsUp className="h-5 w-5" />
                    )}
                    <span className={userReactionEmoji ? "font-bold" : "font-semibold"}>
                      {userReactionEmoji
                        ? userReactionEmoji.label
                        : t("community.post.like_button")}
                    </span>
                  </>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto rounded-full p-1.5 shadow-lg" align="start">
                <div className="flex items-center gap-0.5">
                  {reactions.map((reaction, index) => {
                    const isUserReaction = userReactionEmoji?.emoji === reaction.emoji;
                    return (
                      <Tooltip key={index}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleReaction(reaction.emoji)}
                            className={`flex h-11 w-11 items-center justify-center rounded-full text-2xl transition-transform duration-150 hover:-translate-y-1 hover:scale-125 active:scale-95 ${
                              isUserReaction
                                ? "bg-brand-accent ring-primary ring-2"
                                : "hover:bg-accent"
                            }`}
                            type="button"
                          >
                            {reaction.emoji}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {reaction.label}
                            {isUserReaction ? " (Selected)" : ""}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            <Button
              variant="ghost"
              className="hover:bg-primary/10 hover:text-primary flex-1 gap-2 px-1 py-1 transition-all md:px-3 md:py-1.5"
              onClick={() => {
                const newShowComments = !showComments;
                setShowComments(newShowComments);

                if (newShowComments) {
                  trackEvent("community_comments_opened", {
                    post_id: post.id,
                    post_type: post.isEvent ? "event" : "regular",
                    comment_count: localCommentsCount,
                  });
                }
              }}
            >
              <MessageCircle className="h-5 w-5" />
              <span className="font-semibold">
                {t("community.post.comment_button")}{" "}
                {localCommentsCount > 0 && `(${localCommentsCount})`}
              </span>
            </Button>

            <Popover open={showShareMenu} onOpenChange={setShowShareMenu}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="hover:bg-primary/10 hover:text-primary flex-1 gap-2 px-1 py-1 transition-all md:px-3 md:py-1.5"
                >
                  <Share2 className="h-5 w-5" />
                  <span className="font-semibold">{t("community.post.share")}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 rounded-xl p-1.5 shadow-lg" align="start">
                <div className="space-y-1">
                  <button
                    onClick={handleShareToFeed}
                    className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                  >
                    <Rss className="text-primary h-4 w-4" />
                    <span className="font-medium">{t("community.post.share_to_feed")}</span>
                  </button>
                  <button
                    onClick={handleShareToLinkedIn}
                    className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                  >
                    <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                    <span className="font-medium">{t("community.post.share_to_linkedin")}</span>
                  </button>
                  <button
                    onClick={handleShareToWhatsApp}
                    className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                  >
                    <Send className="h-4 w-4 text-[#25D366]" />
                    <span className="font-medium">{t("community.post.share_to_whatsapp")}</span>
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Comments Section */}
          {showComments && (
            <>
              <Separator />
              {isLoadingComments ? (
                <div className="flex flex-col items-center py-8">
                  <Loader2 className="text-primary h-6 w-6 animate-spin" />
                  <p className="text-muted-foreground mt-2 text-sm">
                    {t("community.post.loading_comments")}
                  </p>
                </div>
              ) : (
                <CommentSection
                  postId={post.id}
                  comments={nestedComments}
                  postAuthor={{
                    id: post.author?.id,
                    name: post.author?.full_name || t("community.post.unknown_author"),
                    title:
                      post.author?.job_title ||
                      (post.author?.type === "employer"
                        ? t("community.post.employer_fallback")
                        : t("community.post.job_seeker_fallback")),
                  }}
                  onComment={handleCommentWithRefresh}
                  onEditComment={handleEditCommentWithRefresh}
                  onDeleteComment={handleDeleteCommentWithRefresh}
                  onAddReply={handleAddReplyWithRefresh}
                  onEditReply={handleEditReplyWithRefresh}
                  onDeleteReply={handleDeleteReplyWithRefresh}
                  autoExpandCommentId={autoExpandCommentId}
                  highlightReplyId={highlightReplyId}
                  showMoreButton={
                    hasMoreComments ? (
                      <div className="flex justify-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={loadMoreComments}
                          disabled={isLoadingMoreComments}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          {isLoadingMoreComments ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {t("community.post.loading_more_comments")}
                            </>
                          ) : (
                            <>{t("community.post.show_more_comments")}</>
                          )}
                        </Button>
                      </div>
                    ) : null
                  }
                />
              )}
            </>
          )}
        </CardContent>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("community.post.delete_post_title")}</AlertDialogTitle>
              <AlertDialogDescription>
                {post.isEvent
                  ? t("community.post.delete_post_event_desc")
                  : t("community.post.delete_post_desc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {/* {!post.isEvent && (
              <div className="py-4">
                <label htmlFor="delete-reason" className="text-sm font-medium mb-2 block">
                  Reason for deletion (optional)
                </label>
                <textarea
                  id="delete-reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="e.g., Outdated information, Incorrect details, etc."
                  className="w-full min-h-[80px] px-3 py-2 border rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            )} */}
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteReason("")}>
                {t("community.comments.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("community.post.delete_post")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Cancel Event Confirmation Dialog */}
        <AlertDialog open={showCancelEventDialog} onOpenChange={setShowCancelEventDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("community.events.cancel_event_confirm")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("community.events.cancel_event_confirm_desc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("community.events.no_keep_event")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (pendingCancelEventId) {
                    trackEvent("community_event_cancelled", {
                      post_id: pendingCancelEventId,
                      participant_count: post.eventParticipants?.length || 0,
                    });
                    onCancelEvent?.(pendingCancelEventId);
                  }
                  setShowCancelEventDialog(false);
                  setPendingCancelEventId(null);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("community.events.yes_cancel_event")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Unregister Event Confirmation Dialog */}
        <AlertDialog open={showUnregisterDialog} onOpenChange={setShowUnregisterDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("community.events.leave_event_title")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("community.events.leave_event_desc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("community.comments.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleUnregisterEvent}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("community.events.leave")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Share Users Dialog */}
        <Dialog open={showShareUsersDialog} onOpenChange={setShowShareUsersDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("community.post.shared_by")}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {isLoadingShares ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="flex items-center gap-3 p-2">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-[60%]" />
                        <Skeleton className="h-3 w-[40%]" />
                      </div>
                      <Skeleton className="h-3 w-16" />
                    </div>
                  ))}
                </div>
              ) : shareUsers?.length && shareUsers?.length > 0 ? (
                <div className="max-h-[400px] space-y-3 overflow-y-auto">
                  {shareUsers?.map((share, index) => (
                    <div
                      key={index}
                      className="hover:bg-accent flex items-center gap-3 rounded-lg p-2 transition-colors"
                    >
                      <Avatar
                        className="h-10 w-10 cursor-pointer transition-opacity hover:opacity-80"
                        onClick={() =>
                          share?.author?.id &&
                          share?.author?.type &&
                          handleUserProfileClick(
                            share.author.id,
                            share.author.type,
                            share.author.organization_id,
                          )
                        }
                      >
                        <AvatarImage
                          src={share?.author?.photo_thumbnail_url}
                          alt={share?.author?.full_name}
                        />
                        <AvatarFallback>{share?.author?.full_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p
                          className="cursor-pointer truncate text-sm font-medium hover:underline"
                          onClick={() =>
                            share?.author?.id &&
                            share?.author?.type &&
                            handleUserProfileClick(
                              share.author.id,
                              share.author.type,
                              share.author.organization_id,
                            )
                          }
                        >
                          {share?.author?.full_name}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {share?.author?.job_title ||
                            (share?.author?.type === "job_seeker" ? "Job Seeker" : "Employer")}
                        </p>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        {formatTimestamp(new Date(share?.created_at))}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  {t("community.post.no_share_info")}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Reaction Users Dialog */}
        <Dialog open={showReactionUsersDialog} onOpenChange={setShowReactionUsersDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("community.post.reactions")}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {isLoadingReactions ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="flex items-center gap-3 p-2">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-[60%]" />
                        <Skeleton className="h-3 w-[40%]" />
                      </div>
                      <Skeleton className="h-6 w-6" />
                    </div>
                  ))}
                </div>
              ) : reactionUsers?.length && reactionUsers?.length > 0 ? (
                <div className="max-h-[400px] space-y-3 overflow-y-auto">
                  {reactionUsers?.map((reaction, index) => (
                    <div
                      key={index}
                      className="hover:bg-accent flex items-center gap-3 rounded-lg p-2 transition-colors"
                    >
                      <Avatar
                        className="h-10 w-10 cursor-pointer transition-opacity hover:opacity-80"
                        onClick={() =>
                          reaction?.user?.id &&
                          reaction?.user?.type &&
                          handleUserProfileClick(
                            reaction.user.id,
                            reaction.user.type,
                            reaction.user.organization_id,
                          )
                        }
                      >
                        <AvatarImage
                          src={reaction?.user?.photo_thumbnail_url}
                          alt={reaction?.user?.full_name}
                        />
                        <AvatarFallback>{reaction?.user?.full_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p
                          className="cursor-pointer truncate text-sm font-medium hover:underline"
                          onClick={() =>
                            reaction?.user?.id &&
                            reaction?.user?.type &&
                            handleUserProfileClick(
                              reaction.user.id,
                              reaction.user.type,
                              reaction.user.organization_id,
                            )
                          }
                        >
                          {reaction?.user?.full_name}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {reaction?.user?.job_title ||
                            (reaction?.user?.type === "job_seeker" ? "Job Seeker" : "Employer")}
                        </p>
                      </div>
                      <span className="text-lg">{reaction?.emoji}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  {t("community.post.no_reactions")}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Lightbox */}
        <Dialog
          open={!!lightboxImageUrl}
          onOpenChange={(open) => !open && setLightboxImageUrl(null)}
        >
          <DialogContent
            hideCloseButton
            className="flex max-w-[95vw] items-center justify-center border-0 bg-transparent p-0 shadow-none sm:max-w-4xl"
          >
            <DialogHeader className="sr-only">
              <DialogTitle>{t("community.post.image_preview")}</DialogTitle>
            </DialogHeader>
            {lightboxImageUrl && (
              <div className="relative inline-block max-w-full">
                <img
                  src={lightboxImageUrl}
                  alt={t("community.post.image_preview")}
                  className="block max-h-[90vh] w-auto max-w-full rounded-lg object-contain"
                />
                <button
                  type="button"
                  onClick={() => setLightboxImageUrl(null)}
                  aria-label={t("community.post.close_image_preview")}
                  title={t("community.post.close_image_preview")}
                  className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white transition hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Report Dialog */}
        <ReportDialog
          open={showReportDialog}
          onOpenChange={setShowReportDialog}
          targetId={post.id}
          targetType={post.isEvent ? "event" : "post"}
        />
      </Card>
    </TooltipProvider>
  );
}
